import VueRouter from 'vue-router';

import { DEFAULT_PAGE_LOCATION } from '@/authentication/services/locations';

import { AuthenticationRouteNames } from '../route-definitions';

import { queryStringWithRedirectParam } from './interfaces';

import { isAuthRequiredForAnyMatchedRoute } from './transition-guard';
import { ServiceTokens } from '@/di/tokens';
import environment from '@/environment';
import { SingleSignOn } from '@/authentication/services/single-sign-on-plugin';
import { Platform } from '@/common/services/get-device-platform';
import type { SSOOrigin } from '../use-cases/login-sso';

export class VueRouterRedirectionService {
    public static inject = [ServiceTokens.VueRouter] as const;

    public constructor(private readonly router: VueRouter) {}

    public hasRedirectParamInQuery(): boolean {
        const query = this.router.currentRoute.query as queryStringWithRedirectParam;
        return typeof query.redirect === 'string';
    }

    public isOnLoginPage() {
        return this.router.currentRoute.name === AuthenticationRouteNames.LOGIN;
    }

    public isAuthRequiredForCurrentRoute() {
        return isAuthRequiredForAnyMatchedRoute(this.router.currentRoute.matched);
    }

    public async redirectToPagePassedInQuery() {
        const query = this.router.currentRoute.query as queryStringWithRedirectParam;
        await this.router.push({ path: query.redirect });
    }

    public async redirectToDefaultPage() {
        await this.router.push(DEFAULT_PAGE_LOCATION);
    }

    public async redirectToLoginPage(isHard = false) {
        if (!isHard) {
            await this.router.push({ name: AuthenticationRouteNames.LOGIN });
        } else {
            const route = this.router.resolve({ name: AuthenticationRouteNames.LOGIN });
            window.location.replace(route.href);
        }
    }

    public async redirectToLoginSsoPage(platformId: number, origin: SSOOrigin) {
        const { apiBaseUrl } = environment;
        const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl : apiBaseUrl + '/';

        let url = environment.isProduction
            ? `${baseUrl}saml2/${platformId}/init`
            : `${baseUrl}users/external/platform-sso/${platformId}/init`;

        if ([Platform.Android, Platform.Ios].includes(origin.devicePlatform)) {
            url += '/?is_mobile';
        }

        if (origin.devicePlatform === Platform.Android) {
            await SingleSignOn.redirectToIdentityProvider({ url });
        } else {
            window.location.replace(url);
        }
    }
}
